import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";


import { useNavigate, useSearch } from "@tanstack/react-router";
import { GiftIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import {
  useDeactivateRewardMutation,
  useRewardsQuery,
  useRewardsSummaryQuery,
} from "../api/queries";
import { RewardFormModal } from "../ui/RewardFormModal";
import { RewardsFilter } from "../ui/RewardsFilter";
import { RewardsSummaryCards } from "../ui/RewardsSummaryCards";
import { RewardsTable } from "../ui/RewardsTable";

type RewardsSearch = {
  isActive?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export function RewardsPage() {
  const nav = useNavigate({ from: "/app/loyalty/rewards" });
  const search = useSearch({ from: "/app/loyalty/rewards" }) as RewardsSearch;
  const canManage = useActionPermission(["loyalty:update"]);
  const [formModal, setFormModal] = useState<{
    open: boolean;
    rewardId: string | null;
  }>({ open: false, rewardId: null });

  const params: Parameters<typeof useRewardsQuery>[0] = {
    isActive:
      search.isActive === "true"
        ? "true"
        : search.isActive === "false"
          ? "false"
          : undefined,
    search: search.search,
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sortField: (search.sortField ?? "name") as
      | "name"
      | "requiredPoints"
      | "createdAt",
    sortOrder: (search.sortOrder ?? "asc") as "asc" | "desc",
  };

  const { data, isLoading } = useRewardsQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useRewardsSummaryQuery();
  const deactivateReward = useDeactivateRewardMutation();

  const items = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາງວັນ",
              to: "/app/loyalty/rewards",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <GiftIcon className="size-5" />
              ລາງວັນ
            </h2>
            <p className="text-muted-foreground">
              ຈັດການລາງວັນທີ່ລູກຄ້າແລກດ້ວຍແຕ້ມ (ຂองຟຣີເທົ່ານັ້ນ).
            </p>
          </div>
          {canManage && (
            <Button
              onClick={() => setFormModal({ open: true, rewardId: null })}
            >
              <PlusIcon className="size-4" />
              ສ້າງລາງວັນ
            </Button>
          )}
        </div>

        <div className="mb-4">
          <RewardsSummaryCards
            totalRewards={summaryData?.totalRewards ?? 0}
            activeCount={summaryData?.activeCount ?? 0}
            avgRedemption={summaryData?.avgRedemption ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <RewardsFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <RewardsTable
              data={items}
              isLoading={isLoading}
              offset={params.offset ?? 0}
              limit={params.limit ?? 20}
              totalCount={total}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onEdit={(row) => setFormModal({ open: true, rewardId: row.id })}
              onDeactivate={async (id) => {
                await toast.promise(deactivateReward.mutateAsync(id), {
                  loading: "ກຳລັງປິດໃຊ້ງານ...",
                  success: "ປິດໃຊ້ງານລາງວັນແລ້ວ",
                  error: "ປິດໃຊ້ງານລົ້ມເຫຼວ",
                });
              }}
            />
          )}
        </div>

        <RewardFormModal
          open={formModal.open}
          onOpenChange={(open) =>
            setFormModal((prev) => ({
              ...prev,
              open,
              rewardId: open ? prev.rewardId : null,
            }))
          }
          rewardId={formModal.rewardId}
        />
      </Main>
    </>
  );
}
